import { CSSObjectWithLabel, StylesConfig } from 'react-select';
import theme from '../../assets/css/theme';

interface IOptions {
  /** Padding inside the closed control. */
  valueContainerPadding?: string;
  /** Padding on each menu row. Keep it aligned with `valueContainerPadding`. */
  optionPadding?: string;
  /** Merged into the closed control, e.g. previewing the chosen font. */
  controlExtra?: CSSObjectWithLabel;
  /** Merged into each menu row, e.g. rendering every row in its own font. */
  optionExtra?: (data: any) => CSSObjectWithLabel;
  /** Merged into the chevron. */
  dropdownIndicatorExtra?: CSSObjectWithLabel;
}

/**
 * The pill-shaped `react-select` look shared by every dropdown in the design tab:
 * a 44px-high, 22px-radius control over a menu of 48px rounded rows.
 *
 * Rows are deliberately grey in all three states — react-select's default focus
 * colour is blue, which clashed with the rest of the panel.
 */
export const createPillSelectStyles = ({
  valueContainerPadding = '2px 18px',
  optionPadding = '9px 18px',
  controlExtra,
  optionExtra,
  dropdownIndicatorExtra
}: IOptions = {}): StylesConfig<any, false> => ({
  control: (styles, { isFocused }) => ({
    ...styles,
    minHeight: '44px',
    borderRadius: '22px',
    cursor: 'pointer',
    '&:hover': {
      borderColor: isFocused ? theme.formsColors.secondColor : styles.borderColor
    },
    ...(isFocused
      ? {
          borderColor: theme.formsColors.secondColor,
          outline: 0,
          boxShadow: '0 0 0 0.2rem rgba(92, 135, 98, 0.25)'
        }
      : {}),
    ...controlExtra
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: valueContainerPadding
  }),
  menu: (styles) => ({
    ...styles,
    zIndex: 999999
  }),
  option: (styles, { data, isSelected, isFocused }) => ({
    ...styles,
    display: 'flex',
    alignItems: 'center',
    minHeight: '48px',
    padding: optionPadding,
    borderRadius: '12px',
    cursor: 'pointer',
    // `isFocused` covers both hover and keyboard navigation.
    backgroundColor: (isSelected && '#f1f1f1') || (isFocused && '#f5f5f5') || '#fff',
    // react-select colours the selected row's text white, which its default blue
    // background hid. On these grey rows that would be invisible.
    color: theme.commonColors.main,
    ...(optionExtra ? optionExtra(data) : {})
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none'
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    paddingRight: '19px'
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    padding: 0,
    ...dropdownIndicatorExtra
  })
});

export default createPillSelectStyles;
